import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import ConfirmDeleteModal from '@/ui/components/confirm-delete-modal/ConfirmDeleteModal';
import {
  Button,
  Divider,
  EmptyState,
  Field,
  Form,
  RawInput,
} from '@payaca/ambrosia';
import { SortableList } from '@payaca/dnd';
import { useDebounceCallback, useScroll } from '@payaca/hooks';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import useHasMoreBelow from '../../useHasMoreBelow';
import {
  useOptimisticReorderEstimateLineItems,
  useOptimisticUpdateEstimateSection,
} from '../api/mutations';
import { EstimateLineItem } from '../types';
import EstimateItemRow from './EstimateItemRow';

type SectionEditorProps = {
  projectId: string;
  estimateId: string;
  sectionId: string;
  handleConfirmDelete: (sectionToDeleteId: string) => void;
  onAddLineItem: (sectionId: string) => void;
  onEditLineItem: (lineItemId: string) => void;
};

const SectionEditor: FC<SectionEditorProps> = (props) => {
  const {
    projectId,
    estimateId,
    sectionId,
    handleConfirmDelete,
    onAddLineItem,
    onEditLineItem,
  } = props;

  /**
   * queries
   */
  const { estimate } = useGetProjectEstimate(projectId, estimateId);
  const section = estimate?.sections.find((s) => s.id === sectionId);

  /**
   * state
   */
  const [sectionToDeleteId, setSectionToDeleteId] = useState<string | null>(
    null
  );

  /**
   * mutations
   */
  const updateEstimateSection = useOptimisticUpdateEstimateSection({
    projectId,
    estimateId,
  });
  const reorderEstimateLineItems = useOptimisticReorderEstimateLineItems({
    projectId,
    estimateId,
  });

  /**
   * form state
   */
  const methods = useForm({
    defaultValues: {
      title: section?.heading ?? '',
    },
  });

  /**
   * subscribe to all form changes. and debounce mutations. (auto-save)
   */
  // Track the last value we sent rather than comparing against `section.heading`
  // or the form's `dirtyFields`: both reset/flip when the user types back to the
  // original heading, which would otherwise drop the "revert" save.
  const lastSavedTitleRef = useRef(section?.heading ?? '');

  const debouncedSaveTitle = useDebounceCallback((title: string) => {
    if (title === lastSavedTitleRef.current) return;
    lastSavedTitleRef.current = title;
    updateEstimateSection.mutate({ id: sectionId, heading: title });
  }, 500);

  const { subscribe } = methods;
  useEffect(() => {
    const unsubscribe = subscribe({
      name: ['title'],
      formState: { values: true },
      callback: ({ values }) => {
        debouncedSaveTitle(values.title);
      },
    });
    return () => {
      unsubscribe();
      debouncedSaveTitle.cancel();
    };
  }, [subscribe, debouncedSaveTitle]);

  /**
   * callbacks
   */
  const handleLineItemDragEnd = (
    _activeItem: EstimateLineItem,
    _destinationIndex: number,
    items: EstimateLineItem[]
  ) => {
    reorderEstimateLineItems.mutate({
      sectionId,
      lineItemIds: items.map((li) => li.id),
    });
  };

  // Defensive: the parent map only renders us for sections that exist in
  // the cache, so this should never fall through in practice.
  if (!section) return null;

  const lineItems = section.lineItems;

  return (
    <>
      <div className="typography:prose flex flex-col min-h-full">
        <Form
          methods={methods}
          onSubmit={() => {}}
          className="px-3.5 pb-3.5 z-30"
        >
          <Field name="title">
            <Field.Label>Section title</Field.Label>

            <RawInput sizeVariant="sm" {...methods.register('title')} />
          </Field>
        </Form>

        <div className="flex-1 min-h-0">
          <h4
            id={`estimate-section-line-items-label-${sectionId}`}
            className="px-3.5 pb-2"
          >
            Section items ({lineItems.length})
          </h4>

          <SortableList
            id={`estimate-section-line-items-${sectionId}`}
            labelledBy={`estimate-section-line-items-label-${sectionId}`}
            className="flex flex-col divide-y"
            items={lineItems}
            onDragEnd={handleLineItemDragEnd}
            lockAxis
            renderItem={(lineItem) => (
              <EstimateItemRow
                lineItem={lineItem}
                onClick={() => onEditLineItem(lineItem.id)}
              />
            )}
            emptyState={
              <EmptyState className="my-4" text="No items in this section" />
            }
          />

          <div className="px-3.5 py-4 sticky bottom-0 z-30 bg-white">
            <Button
              variant="dashed"
              size="sm"
              className="w-full"
              onClick={() => onAddLineItem(sectionId)}
            >
              <UntitledIcon name="plus.3" className="size-4" />
              Add item
            </Button>
          </div>
        </div>

        <div className="px-3.5 py-4 border-t">
          <Button
            variant="dashed"
            colour="red"
            size="sm"
            className="w-full"
            onClick={() => setSectionToDeleteId(sectionId)}
          >
            Remove section
          </Button>
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={sectionToDeleteId !== null}
        onClose={() => setSectionToDeleteId(null)}
        onConfirmDelete={() => {
          if (!sectionToDeleteId) return;
          handleConfirmDelete(sectionToDeleteId);
          setSectionToDeleteId(null);
        }}
        entityName={{ singular: 'section', plural: 'sections' }}
        destructiveVerb="remove"
      />
    </>
  );
};

export default SectionEditor;
